import { useGA } from "@customer-tools-web/google-analysis-tracking";
import { useMarketConfig } from "@customer-tools-web/market-extension-resolver";
import { get, isEmpty, isNil, isString, mapValues, omitBy, trim } from "lodash";
import * as React from "react";

import {
  listingsQuery_listings_data,
  listingQuery_listing,
} from "../../../__generated__/listings";

import { BaseEvent, EventMarketType } from "./const";
import { parseReference } from "./trackTools";
import { useSearchQuery } from "./withSearchQuery";
import { useUserInfo } from "./withUserInfo";

interface GAListingSpec {
  propertyType: {
    code: number;
    label: string | null;
  };
  id: string;
  channel: {
    code: number;
    label: string | null;
  };
  organisationId: string | null;
  listerId: string | null;
  representationListerId: string | null;
  location?: {
    level1: { text: { en_GB: string | null } } | null;
    level2: { text: { en_GB: string | null } } | null;
    level3: { text: { en_GB: string | null } } | null;
    level5: { text: { en_GB: string | null } } | null;
    address: { en_GB: string | null } | null;
  } | null;
  salePrice?: {
    fixed: number | null;
  } | null;
  rentPrice?: {
    fixed: number | null;
  } | null;
  listingProduct?: { code: number } | null;
}

interface GACredit {
  listing_credit?: string | null;
  ad_credit?: string | null;
  featured_credit?: string | null;
}

type OptionalParamsType = {
  listing?:
    | listingQuery_listing
    | listingsQuery_listings_data
    | null
    | GAListingSpec;
  tier?: TierType | null;
  shouldWithSearch?: boolean;
  credit?: GACredit | null;
};

type TriggerGAEventProps = {
  triggerGAEvent: (event: BaseEvent, options?: OptionalParamsType) => void;
};

function withTriggerGAEvent<
  Props extends TriggerGAEventProps = TriggerGAEventProps
>(
  Component: React.ComponentType<Props>
): React.ComponentType<Omit<Props, keyof TriggerGAEventProps>> {
  return function TriggerGAEventWrapper(
    props: Omit<Props, keyof TriggerGAEventProps>
  ) {
    const triggerGAEvent = useTriggerGAEvent();
    return <Component {...(props as Props)} triggerGAEvent={triggerGAEvent} />;
  };
}

export function useTriggerGAEvent() {
  const marketConfig = useMarketConfig();
  const userEvent = useUserInfo();
  const { buildSearchConditions } = useSearchQuery();
  const { triggerEvent } = useGA();
  function triggerGAEvent(event: BaseEvent, options?: OptionalParamsType) {
    if (
      event &&
      (!event.market || event.market.indexOf(marketConfig.market) > -1)
    ) {
      const gaEvent = formatEvent({
        ...event,
        market: undefined,
        ...(isEmpty(userEvent) ? {} : userEvent),
        ...(options &&
          parseListing(options.listing, options.tier, marketConfig.market)),
        ...(options && options.shouldWithSearch ? buildSearchConditions() : {}),
        ...(options && options.credit),
      });
      triggerEvent(gaEvent);
    }
  }
  function formatEvent(event: object) {
    const noEmptyEvent = omitBy(event, (value: any) => !value);
    return mapValues(noEmptyEvent, (value) =>
      isString(value) ? trim(value) : value
    );
  }

  return triggerGAEvent;
}

type TierType = {
  id: string;
  rent: {
    quoteIds: string[];
    productDuration: number;
    productCode: number;
    title: string;
    remaining: number;
    cost: number | null;
    originalCost: number | null;
    discountPercentage: number | null;
    disabled: boolean;
    listingProduct: { id: string; code: number; label: string | null };
  } | null;
  sale: {
    quoteIds: string[];
    productDuration: number;
    productCode: number;
    title: string;
    remaining: number;
    cost: number | null;
    originalCost: number | null;
    discountPercentage: number | null;
    disabled: boolean;
    listingProduct: { id: string; code: number; label: string | null };
  } | null;
  reaId: string | null;
};

export const parseTier = (tier?: TierType | null) => {
  if (tier) {
    const rentReference = get(tier, "rent.listingProduct");
    const saleReference = get(tier, "sale.listingProduct");
    let tierName = "";
    let tierID = "";

    if (saleReference && rentReference) {
      tierName = `${parseReference(saleReference)}||${parseReference(
        rentReference
      )}`;
      tierID = `${saleReference.code}||${rentReference.code}`;
    } else if (saleReference) {
      tierName = `${parseReference(saleReference)}`;
      tierID = `${saleReference.code}`;
    } else if (rentReference) {
      tierName = `${parseReference(rentReference)}`;
      tierID = `${rentReference.code}`;
    }

    return {
      tierName,
      tierID,
    };
  }
  return {
    tierName: "",
    tierID: "",
  };
};

export const parseListing = (
  listing?: OptionalParamsType["listing"],
  tier?: TierType | null,
  market?: EventMarketType
) => {
  const myExtend =
    market === "MY"
      ? {
          listing_property_location_level_5:
            listing?.location?.level5?.text?.en_GB,
        }
      : {};
  return listing
    ? {
        listing_id: listing.id,
        listing_channel: parseReference(listing?.channel),
        listing_price_rent: !isNil(listing.rentPrice?.fixed)
          ? `${listing.rentPrice?.fixed}`
          : "",
        listing_price_sale: !isNil(listing.salePrice?.fixed)
          ? `${listing.salePrice?.fixed}`
          : "",
        listing_tier_id: (listing.listingProduct
          ? listing.listingProduct.code
          : parseTier(tier).tierID
        ).toString(),
        listing_tier_name: listing.listingProduct
          ? parseReference(listing.listingProduct)
          : parseTier(tier).tierName,
        listing_property_type: parseReference(listing.propertyType),
        listing_property_address_en: listing.location?.address?.en_GB,
        listing_property_location_level_1:
          listing.location?.level1?.text?.en_GB,
        listing_property_location_level_2:
          listing.location?.level2?.text?.en_GB,
        listing_property_location_level_3:
          listing.location?.level3?.text?.en_GB,
        ...myExtend,
        listing_organization_id: listing.organisationId,
        listing_primary_user_id: listing.listerId,
        listing_secondary_user_id: listing.representationListerId,
      }
    : {};
};

export { withTriggerGAEvent, TriggerGAEventProps, TierType, GACredit };
